import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'clsx';
import React from 'react';

import { ReactComponent as DndIcon } from '@assets/svg/dnd.svg';
import { ReactComponent as PageBackIcon } from '@assets/svg/page-back-icon.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import type { IFlattenedItem } from '../../model/location';
import style from './style.module.scss';

interface Props {
  item: IFlattenedItem;
  clone?: boolean;
  indentationWidth: number;
  depth: number;
  sortable: boolean;
  collapsed?: boolean;
  onCollapse?: () => void;
  childrenCount?: number;
  index?: number;
}

const SortableTreeItem: React.FC<Props> = ({
  item,
  clone,
  indentationWidth,
  depth,
  sortable,
  collapsed,
  onCollapse,
  childrenCount,
  index,
}) => {
  const { id, name, parentId } = item;
  const {
    attributes,
    isDragging,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges: ({ isSorting, wasDragging }) => (isSorting || wasDragging ? false : true),
    data: {
      ...item,
      index,
    },
  });

  const { data: assetTreeData } = useAssetTreeQuery();

  const hasEditPermission = !!assetTreeData?.allowedIds[id];
  const isLocationLevel = depth > 0;
  const showActionMenu = !clone && isLocationLevel && hasEditPermission;

  return (
    <div
      className={cx(style.locationWrapper, {
        [style.clone]: clone,
      })}
      ref={setDroppableNodeRef}
      style={{ paddingLeft: indentationWidth * depth }}
    >
      <div
        ref={setDraggableNodeRef}
        className={cx(style.treeItem, {
          [style.locationLevel]: isLocationLevel,
          [style.withOverlay]: isDragging,
        })}
        style={{
          transform: CSS.Translate.toString(transform),
          transition,
        }}
      >
        {isDragging ? (
          <div className={style.over} />
        ) : (
          <>
            <div className={style.locationNameBlock}>
              {/* {sortable && (
                <div {...attributes} {...listeners} className={style.dndIconContainer}>
                  <DndIcon className={style.dndIcon} />
                </div>
              )} */}
              {onCollapse && (
                <span className={style.arrowContainer} onClick={onCollapse}>
                  <PageBackIcon className={cx(style.expandIcon, { [style.closeExpandIcon]: collapsed })} />
                </span>
              )}
              <span className={cx(style.locationName, { [style.locationLevel]: isLocationLevel })}>
                {name}
              </span>
            </div>
            {showActionMenu && (
              <div
                className={style.actionsIconContainer}
                data-tooltip-id="location-action-menu"
                data-tooltip-content={JSON.stringify({ id, name, parentId })}
              >
                <ThreeDotsIcon />
              </div>
            )}
            {clone && childrenCount! > 1 ? <span className={style.count}>{childrenCount}</span> : null}
          </>
        )}
      </div>
    </div>
  );
};

export default SortableTreeItem;
